/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Stack,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import { FieldArray, Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { DrilldownItemCte } from '@components/DrilldownItem';
import { FormikTextField } from '@components/FormikFields';
import IconButton from '@components/IconButton';
import RoundedButton from '@components/RoundedButton/index';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import {
  useAdminCreatePolicy,
  useAdminListPermissions,
  useAdminUpdatePolicyPermissions,
} from 'generated/admin.generated';
import type { PermissionSetting, PolicyModel } from 'generated/types';

const messages = defineMessages({
  name: {
    id: 'admin.policies.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.policies.description',
    defaultMessage: 'Description',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  title: {
    id: 'admin.policies.create.title',
    defaultMessage: 'New Policy',
  },
  permissionTitle: {
    id: 'admin.policies.create.permissions',
    defaultMessage: 'Select Permissions',
  },
  addPermission: {
    id: 'admin.policies.permissions.add',
    defaultMessage: 'Add Permission',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  invalid: {
    id: 'admin.policies.permissions.invalid',
    defaultMessage: 'Select a permission from dropdown',
  },
});

interface PolicyFormProps {
  /** A policy. If provided, the form will update the policy. If empty, a new policy will be created */
  policy?: PolicyModel;
  onCancelEdit?: () => void;
}

type FormValues = {
  name: string;
  description?: string | null;
  permissions: PermissionSetting[];
};

const PolicyForm: React.FC<PolicyFormProps> = ({ policy, onCancelEdit }) => {
  const { formatMessage } = useIntl();
  const { AdminSchema } = useCommonSchemas();
  const [updatePermissions] = useAdminUpdatePolicyPermissions();
  const [createPolicy] = useAdminCreatePolicy();
  const { data: permissionData } = useAdminListPermissions({
    variables: { input: { scope: null } },
  });

  const isEditing = Boolean(policy);

  const permissionScopes = [
    { k: 'default', v: 'Default' },
    { k: 'O', v: 'Organization-wide' },
    { k: '*', v: 'System-wide' },
  ];

  const permissionOptions = permissionData?.listPermissions
    .map((p) => ({
      k: p.permission_id,
      v: `${p.permission_scope}:${p.permission_name}`,
    }))
    .sort((a, b) => a.v.localeCompare(b.v));

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    name: AdminSchema.name(1, 128).required(formatMessage(messages.required)),
    description: AdminSchema.description(1, 1000).nullable(),
    permissions: Yup.array()
      .of(
        Yup.object().shape({
          is_enabled: Yup.boolean().oneOf([true, false]).required(),
          permission_id: Yup.string()
            .uuid(formatMessage(messages.invalid))
            .required(),
          permission_scope: Yup.string().nullable(),
        })
      )
      .required(),
  });

  const initialValues: FormValues = {
    name: policy?.policy_name ?? '',
    description: policy?.policy_description ?? '',
    permissions:
      policy?.permissions?.map(
        ({ is_enabled, permission_id, access_level }) => ({
          is_enabled,
          permission_id,
          permission_scope: access_level ?? 'default',
        })
      ) ?? [],
  };

  async function handleSubmit(
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) {
    const permissions: PermissionSetting[] = values.permissions.map((p) => ({
      ...p,
      permission_scope:
        p.permission_scope === 'default' ? null : p.permission_scope,
      is_enabled: p.is_enabled,
    }));

    if (isEditing && policy) {
      await updatePermissions({
        variables: {
          input: {
            policy_id: policy.policy_id,
            permissions,
          },
        },
      });
      return;
    }

    await createPolicy({
      variables: {
        input: {
          policy_name: values.name,
          policy_description: values.description,
          permissions,
        },
      },
    });
    resetForm();
  }

  const handleCancelEdit = () => {
    onCancelEdit?.();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values, isValid }) => {
        return (
          <Form>
            <Card>
              {!isEditing && (
                <>
                  <CardContent>
                    <Stack direction="row" spacing={2}>
                      <FormikTextField
                        autoComplete="off"
                        label={formatMessage(messages.name)}
                        name="name"
                        fullWidth
                      />
                      <FormikTextField
                        autoComplete="off"
                        label={formatMessage(messages.description)}
                        name="description"
                        fullWidth
                      />
                    </Stack>
                  </CardContent>
                  <Divider />
                </>
              )}
              <CardHeader
                title={formatMessage(messages.permissionTitle)}
                titleTypographyProps={{ variant: 'subtitle1' }}
                action={
                  isEditing && (
                    <IconButton
                      name="xmark"
                      iconType="fas"
                      onClick={handleCancelEdit}
                    />
                  )
                }
              />
              <CardContent>
                <FieldArray
                  name="permissions"
                  render={(arrayHelpers) => {
                    const defaultPermission: PermissionSetting = {
                      is_enabled: true,
                      permission_id: '(None)',
                      permission_scope: 'default',
                    };
                    if (!values.permissions.length) {
                      arrayHelpers.push({ ...defaultPermission });
                    }

                    const handleAdd = () => {
                      arrayHelpers.push({ ...defaultPermission });
                    };

                    const handleDelete = (
                      index: number,
                      permission: PermissionSetting
                    ) => {
                      const isRealPermission =
                        permission.permission_id !==
                        defaultPermission.permission_id;
                      if (isEditing && isRealPermission) {
                        arrayHelpers.replace(index, {
                          ...permission,
                          is_enabled: false,
                        });
                      } else {
                        arrayHelpers.remove(index);
                      }
                    };

                    const errorObj = arrayHelpers.form.errors;
                    const arr = errorObj[arrayHelpers.name] ?? [];

                    return (
                      <Box>
                        {values.permissions.map((permission, index) => {
                          if (permission.is_enabled === false) {
                            return (
                              <Box
                                key={`permission-${index}`}
                                sx={{ py: 0.5 }}
                              >{`${permission.permission_id} will be deleted`}</Box>
                            );
                          }

                          const permissionError =
                            Array.isArray(arr) && arr[index]
                              ? Object.values(arr[index])
                              : null;

                          return (
                            <Stack
                              direction="row"
                              key={`permission-${index}`}
                              spacing={2}
                              alignItems="center"
                            >
                              <DrilldownItemCte
                                label="Permission"
                                name={`permissions.${index}.permission_id`}
                                isEditing
                                options={permissionOptions}
                                type="dropdown"
                                value={permission.permission_id}
                              />
                              <DrilldownItemCte
                                label="Access Level"
                                name={`permissions.${index}.permission_scope`}
                                isEditing
                                type="dropdown"
                                options={permissionScopes}
                                value={permission.permission_scope}
                              />
                              <IconButton
                                name="trash"
                                onClick={() => handleDelete(index, permission)}
                                iconType="fad"
                                sx={{ alignSelf: 'center' }}
                              />
                              {permissionError && (
                                <FormHelperText error>
                                  {permissionError.join(', ')}
                                </FormHelperText>
                              )}
                            </Stack>
                          );
                        })}
                        <RoundedButton
                          autoSize
                          iconName="plus"
                          onClick={handleAdd}
                          type="button"
                        >
                          {formatMessage(messages.addPermission)}
                        </RoundedButton>
                      </Box>
                    );
                  }}
                />
              </CardContent>
              <Divider />
              <CardActions>
                <RoundedButton
                  disabled={isSubmitting || !isValid}
                  type="submit"
                >
                  {formatMessage(messages.submit)}
                </RoundedButton>
              </CardActions>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PolicyForm;
